/* ============================================================
   GABBIANO CUSTOM — Variant thumbnail swatches on card hover
   ------------------------------------------------------------
   Les Deux style: on hover, up to 4 small variant photos appear
   bottom-left over the card image. Hovering a thumbnail swaps
   the main card image to that colour. A "+N" chip links through
   to the product page when there are more than 4 colours.

   Self-contained: reads the product handle from the theme's own
   <product-card handle="..."> element and fetches variant data
   from /products/<handle>.js on hover, then caches it. Nothing
   extra is loaded on page load. Does NOT depend on the theme's
   own colour swatches, so it works regardless of how the colour
   option is named or translated.

   When the pointer leaves the card, the original image (src +
   srcset) and the theme's native hover-swap are fully restored,
   so the theme's own second-image-on-hover keeps working.

   17 Sep 2026: colours without a single purchasable size are no
   longer shown (variant.available check in colorGroups), so
   not-yet-delivered washes stay hidden until stock arrives.
   ============================================================ */
(function () {
  'use strict';

  window.GB_PRODUCT_CACHE = window.GB_PRODUCT_CACHE || {};

  var MAX = 4;
  var COLOR_NAMES = ['color', 'colour', 'kleur', 'couleur', 'farbe', 'colore'];

  function getProduct(handle) {
    if (window.GB_PRODUCT_CACHE[handle]) return window.GB_PRODUCT_CACHE[handle];

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    window.GB_PRODUCT_CACHE[handle] = fetch(root + 'products/' + handle + '.js')
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; });

    return window.GB_PRODUCT_CACHE[handle];
  }

  function optionNames(product) {
    if (!product || !product.options) return [];
    return product.options.map(function (option) {
      return String(typeof option === 'string' ? option : option.name || '').toLowerCase().trim();
    });
  }

  function colorIndexOf(product) {
    var names = optionNames(product);
    for (var i = 0; i < names.length; i++) {
      if (COLOR_NAMES.indexOf(names[i]) !== -1) return i;
    }
    return -1;
  }

  function optionValue(variant, index) {
    var values = variant.options || [variant.option1, variant.option2, variant.option3];
    return values[index];
  }

  function sized(url, width) {
    if (!url) return '';
    var clean = String(url).indexOf('//') === 0 ? 'https:' + url : String(url);
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Build one entry per colour: its label, its thumbnail image and the
     full-size image to show when hovered. Falls back gracefully when a
     variant has no dedicated image. */
  function colorGroups(product) {
    var index = colorIndexOf(product);
    if (index === -1) return [];

    var order = [];
    var byColor = {};

    var available = {};

    product.variants.forEach(function (variant) {
      var color = optionValue(variant, index);
      if (!color) return;

      if (!(color in byColor)) {
        order.push(color);
        byColor[color] = null;
        available[color] = false;
      }

      /* A colour counts as "online" as soon as one of its sizes can be
         bought. Colours whose every size is sold out or not yet in stock
         (e.g. a wash that has not been delivered yet) are skipped, so the
         card matches the product page, which hides sold-out variants. */
      if (variant.available) available[color] = true;

      var image = variant.featured_image && variant.featured_image.src;
      if (image && !byColor[color]) byColor[color] = image;
    });

    return order.map(function (color) {
      return { label: color, image: byColor[color], available: available[color] };
    }).filter(function (group) {
      return group.image && group.available;
    });
  }

  /* Put the card back exactly as the theme rendered it, so the native
     hover-swap (second image on hover) works again after we leave. */
  function resetCard(card) {
    var image = card.querySelector('.product-card__image--primary');

    if (image && image.dataset.gbOriginalSrc !== undefined) {
      image.setAttribute('src', image.dataset.gbOriginalSrc);

      if (image.dataset.gbOriginalSrcset) {
        image.setAttribute('srcset', image.dataset.gbOriginalSrcset);
      }
      if (image.dataset.gbOriginalSizes) {
        image.setAttribute('sizes', image.dataset.gbOriginalSizes);
      }
    }

    var link = card.querySelector('.product-card__media');
    if (link) link.classList.remove('product-card__media--browsing');

    var active = card.querySelector('.product-card__thumb[aria-current="true"]');
    var thumbs = card.querySelectorAll('.product-card__thumb');
    thumbs.forEach(function (thumb, i) {
      thumb.setAttribute('aria-current', i === 0 ? 'true' : 'false');
    });
  }

  function buildThumbs(figure, product) {
    if (figure.querySelector('.product-card__thumbs')) return;

    var groups = colorGroups(product);
    if (groups.length < 2) return;

    var link = figure.querySelector('.product-card__media');
    var image = figure.querySelector('.product-card__image--primary');
    if (!link || !image) return;

    var container = document.createElement('div');
    container.className = 'product-card__thumbs';

    var shown = groups.slice(0, MAX);

    shown.forEach(function (group, i) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'product-card__thumb';
      button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      button.setAttribute('aria-label', group.label);
      button.title = group.label;
      button.style.backgroundImage = 'url("' + sized(group.image, 120) + '")';
      button.dataset.full = sized(group.image, 900);

      button.addEventListener('pointerenter', function () {
        /* Remember the theme's original image the first time we touch it */
        if (image.dataset.gbOriginalSrc === undefined) {
          image.dataset.gbOriginalSrc = image.getAttribute('src') || '';
          image.dataset.gbOriginalSrcset = image.getAttribute('srcset') || '';
          image.dataset.gbOriginalSizes = image.getAttribute('sizes') || '';
        }

        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.setAttribute('src', button.dataset.full);
        link.classList.add('product-card__media--browsing');

        container.querySelectorAll('.product-card__thumb').forEach(function (other) {
          other.setAttribute('aria-current', other === button ? 'true' : 'false');
        });
      });

      container.appendChild(button);
    });

    if (groups.length > shown.length) {
      var more = document.createElement('a');
      more.className = 'product-card__thumb-more';
      more.href = link.getAttribute('href') || '#';
      more.textContent = '+' + (groups.length - shown.length);
      more.setAttribute('aria-label', groups.length + ' colours — view all');
      container.appendChild(more);
    }

    figure.appendChild(container);
  }

  function hydrate(card) {
    var figure = card.querySelector('.product-card__figure');
    if (!figure || figure.dataset.gbThumbs) return;

    var handle = card.getAttribute('handle');
    if (!handle) return;

    figure.dataset.gbThumbs = 'pending';

    getProduct(handle).then(function (product) {
      if (!product || !product.variants) { figure.dataset.gbThumbs = 'none'; return; }
      buildThumbs(figure, product);
      figure.dataset.gbThumbs = 'done';
    });
  }

  /* Prepare thumbnails the moment the pointer touches a card */
  document.addEventListener(
    'pointerenter',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var card = target.closest('product-card[handle]');
      if (card) hydrate(card);
    },
    true
  );

  /* Restore the card when the pointer leaves it, so the theme's native
     hover behaviour is never left disabled. */
  document.addEventListener(
    'pointerleave',
    function (event) {
      var target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('product-card[handle]')) resetCard(target);
    },
    true
  );
})();
